import React from 'react';
import { observer } from 'mobx-react-lite';
import { Line, LineChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { apiFetch } from '../../api/apiClient';
import { getAPIUrl } from '../../api/apiUtils';
import TextLengthFilter from '../../components/TextLengthFilter';
import DataWindowSlider from '../../components/DataWindowSliderPV';
import ChartPointDataModal from '../../components/ChartPointDataModal';
import { groupIdToTitle } from '../../utils/groupTitles';

const DAY_MS = 24 * 60 * 60 * 1000;
const CHART_WINDOW_SIZE = 365;

function fillDays(startDate, endDate, rows) {
  const byDate = new Map(rows.map((row) => [row.date, row]));
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return [];
  const result = [];
  for (let timestamp = start; timestamp <= end; timestamp += DAY_MS) {
    const date = new Date(timestamp).toISOString().slice(0, 10);
    result.push({ date, timestamp, ...(byDate.get(date) || {}) });
  }
  return result;
}

const formatDay = (timestamp) => new Date(timestamp).toLocaleDateString('ru-RU', { timeZone: 'UTC' });

function DailyChart({ title, description, textLength, onApplyTextLength, data, sourceCount, loading, error, children }) {
  const [windowStart, setWindowStart] = React.useState(0);
  React.useEffect(() => setWindowStart(0), [data]);
  const start = Math.min(windowStart, Math.max(0, data.length - CHART_WINDOW_SIZE));
  const visibleData = data.slice(start, start + CHART_WINDOW_SIZE);
  const dateRangeText = visibleData.length
    ? `${formatDay(visibleData[0].timestamp)} – ${formatDay(visibleData[visibleData.length - 1].timestamp)}` : '';

  return (
    <section style={{ marginTop: 28 }}>
      <h3>{title}</h3>
      <p className="text-muted small">{description}</p>
      <TextLengthFilter value={textLength} onApply={onApplyTextLength} />
      {error ? (
        <div className="text-danger">{error}</div>
      ) : loading ? (
        <div className="text-muted">Данные загружаются...</div>
      ) : !data.length ? (
        <div className="text-muted">Нет данных для отображения</div>
      ) : (
        <>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <DataWindowSlider
            aggregation="day"
            dataWindow={{ start, end: start + CHART_WINDOW_SIZE }}
            maxDataLength={data.length}
            windowSize={CHART_WINDOW_SIZE}
            updateWindow={(value) => setWindowStart(Number(value))}
            dateRangeText={dateRangeText}
          />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={visibleData} margin={{ top: 15, right: 30, left: 5, bottom: 25 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={formatDay} minTickGap={40} />
            <YAxis yAxisId="count" allowDecimals={false} />
            <Tooltip labelFormatter={formatDay} />
            <Legend verticalAlign="top" height={32} />
            {children}
          </LineChart>
        </ResponsiveContainer>
        </>
      )}
      {!loading && !error && sourceCount != null && (
        <div className="mt-2 text-muted small" style={{ textAlign: 'right' }}>
          Обработано записей: {sourceCount.toLocaleString('ru-RU')}
        </div>
      )}
    </section>
  );
}

function useDailySeries({ startDate, endDate, groupIds, textLength, metric, authorRequests }) {
  const [state, setState] = React.useState({ data: [], eligibleAuthors: 0, sourceCount: null, loading: false, error: null });
  const { min, max } = textLength;

  React.useEffect(() => {
    const controller = new AbortController();
    if (!startDate || !endDate || !groupIds) {
      setState({ data: [], eligibleAuthors: 0, sourceCount: null, loading: false, error: null });
      return () => controller.abort();
    }
    setState({ data: [], eligibleAuthors: 0, sourceCount: null, loading: true, error: null });
    const load = async () => {
      if (controller.signal.aborted) return;
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        group_ids: groupIds,
        min_text_length: min,
        max_text_length: max,
      });
      if (metric) params.set('metric', metric);
      const path = metric ? '/detection/user_marker_outliers_daily' : '/vk/activity/daily';
      try {
        const response = await apiFetch(`${getAPIUrl()}${path}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (controller.signal.aborted) return;
        const rows = result.data || [];
        const hasData = metric ? result.eligible_authors : rows.length;
        const data = hasData ? fillDays(startDate, endDate, rows).map((row) => metric
          ? { ...row, authors: row.authors || 0 }
          : { ...row, posts: row.posts || 0, comments: row.comments || 0, likes: row.likes || 0 }) : [];
        setState({ data, eligibleAuthors: result.eligible_authors || 0, sourceCount: result.source_count ?? null, loading: false, error: null });
      } catch (error) {
        if (!controller.signal.aborted) setState({ data: [], eligibleAuthors: 0, sourceCount: null, loading: false, error: error.message });
      }
    };
    // Keep the two expensive author aggregations sequential.
    if (metric) {
      authorRequests.current = authorRequests.current.then(load, load);
    } else {
      load();
    }
    return () => controller.abort();
  }, [startDate, endDate, groupIds, min, max, metric, authorRequests]);

  return state;
}

const DynamicsContextCharts = observer(() => {
  const filters = useGlobalFilters();
  const filtersKey = filters.filtersString;
  const [tragerLength, setTragerLength] = React.useState({ min: '100', max: '' });
  const [actionLength, setActionLength] = React.useState({ min: '100', max: '' });
  const [activityLength, setActivityLength] = React.useState({ min: '', max: '' });
  const authorRequests = React.useRef(Promise.resolve());
  const common = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    groupIds: filters.selectedGroups.join(','),
    authorRequests,
  };
  const tragerChart = useDailySeries({ ...common, textLength: tragerLength, metric: 'trager_coef' });
  const actionChart = useDailySeries({ ...common, textLength: actionLength, metric: 'verbs_per_nouns_coef' });
  const activityChart = useDailySeries({ ...common, textLength: activityLength });
  const [details, setDetails] = React.useState(null);
  const detailsRequest = React.useRef(null);

  const closeDetails = () => {
    detailsRequest.current?.abort();
    setDetails(null);
  };

  const openDetails = async (chart, point) => {
    if (!point?.date || (chart !== 'activity' ? !point.authors : !(point.posts || point.comments || point.likes))) return;
    detailsRequest.current?.abort();
    const controller = new AbortController();
    detailsRequest.current = controller;
    setDetails({ date: point.date, chart, rows: [], loading: true });
    const params = new URLSearchParams({
      date: point.date,
      group_ids: filters.selectedGroups.join(','),
    });
    const bounds = chart === 'activity' ? activityLength : chart === 'action' ? actionLength : tragerLength;
    params.set('min_text_length', bounds.min);
    params.set('max_text_length', bounds.max);
    if (chart !== 'activity') {
      params.set('start_date', filters.startDate);
      params.set('end_date', filters.endDate);
      params.set('metric', chart === 'action' ? 'verbs_per_nouns_coef' : 'trager_coef');
    }
    const path = chart !== 'activity'
      ? '/detection/user_marker_outliers_daily/details'
      : '/vk/activity/daily/details';
    try {
      const response = await apiFetch(`${getAPIUrl()}${path}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (!controller.signal.aborted) setDetails({
        date: point.date,
        chart,
        rows: (result.data || []).map((row) => ({
          ...row,
          Группа: groupIdToTitle[String(row.Группа)] || row.Группа,
        })),
        loading: false,
      });
    } catch (error) {
      if (!controller.signal.aborted) setDetails({ date: point.date, chart, rows: [], loading: false, error: `Не удалось загрузить данные: ${error.message}` });
    }
  };

  const pointHandler = (chart) => ({
    r: 6,
    cursor: 'pointer',
    onMouseDown: (_event, dot) => openDetails(chart, dot.payload),
  });

  React.useEffect(() => {
    detailsRequest.current?.abort();
    setDetails(null);
    return () => detailsRequest.current?.abort();
  }, [filtersKey, tragerLength, actionLength, activityLength]);

  if (!filters.selectedGroups.length) return null;
  return (
    <>
      <DailyChart
        title="Авторы с отклонением коэффициента Трейгера"
        description={`Число авторов за день, у которых средний коэффициент выше личного среднего за выбранный диапазон дат +1σ или ниже среднего −1σ. В каждой точке графика за день один автор учитывается максимум один раз, независимо от количества его комментариев и ответов. Этот же автор может учитываться в другие дни. Учитываются комментарии и ответы с выбранной длиной текста. Подходящих авторов: ${tragerChart.eligibleAuthors}. Нажмите на точку, чтобы увидеть комментарии и ответы за соответствующим порогом, сгруппированные по постам.`}
        textLength={tragerLength}
        onApplyTextLength={setTragerLength}
        data={tragerChart.data}
        sourceCount={tragerChart.sourceCount}
        loading={tragerChart.loading}
        error={tragerChart.error}
      >
        <Line yAxisId="count" type="linear" dataKey="authors" name="Авторов" stroke="#b91c1c" strokeWidth={2} dot={false} activeDot={pointHandler('trager')} />
      </DailyChart>
      <DailyChart
        title="Авторы с отклонением коэффициента опредмеченности действия"
        description={`Число авторов за день, у которых средний коэффициент опредмеченности действия вышел за личный диапазон ±1σ, рассчитанный по выбранным датам. В каждой точке графика за день один автор учитывается максимум один раз, независимо от количества его комментариев и ответов. Этот же автор может учитываться в другие дни. Учитываются комментарии и ответы с выбранной длиной текста. Подходящих авторов: ${actionChart.eligibleAuthors}. Нажмите на точку, чтобы увидеть комментарии и ответы за соответствующим порогом, сгруппированные по постам.`}
        textLength={actionLength}
        onApplyTextLength={setActionLength}
        data={actionChart.data}
        sourceCount={actionChart.sourceCount}
        loading={actionChart.loading}
        error={actionChart.error}
      >
        <Line yAxisId="count" type="linear" dataKey="authors" name="Авторов" stroke="#7c3aed" strokeWidth={2} dot={false} activeDot={pointHandler('action')} />
      </DailyChart>
      <DailyChart
        title="Активность в сообществах по дням"
        description="Посты и комментарии, включая ответы, считаются по дате создания. Лайки — текущее число лайков у постов, опубликованных в этот день; дата постановки лайка неизвестна. Нажмите на точку любой линии, чтобы увидеть посты за день."
        textLength={activityLength}
        onApplyTextLength={setActivityLength}
        data={activityChart.data}
        sourceCount={activityChart.sourceCount}
        loading={activityChart.loading}
        error={activityChart.error}
      >
        <YAxis yAxisId="likes" orientation="right" allowDecimals={false} />
        <Line yAxisId="count" type="linear" dataKey="posts" name="Посты" stroke="#2563eb" strokeWidth={2} dot={false} activeDot={pointHandler('activity')} />
        <Line yAxisId="count" type="linear" dataKey="comments" name="Комментарии" stroke="#16a34a" strokeWidth={2} dot={false} activeDot={pointHandler('activity')} />
        <Line yAxisId="likes" type="linear" dataKey="likes" name="Лайки на постах" stroke="#d97706" strokeWidth={2} dot={false} activeDot={pointHandler('activity')} />
      </DailyChart>
      {details && <ChartPointDataModal date={details.date} rows={details.rows} loading={details.loading} error={details.error} groupByPost={details.chart !== 'activity'} onClose={closeDetails} />}
    </>
  );
});

export default DynamicsContextCharts;
